/**
 * 工具调用 → 业务语言展示映射
 *
 * SSE tool_start / tool_end 事件里的 name / args / result 都是 LLM 友好的工程
 * 字段，直接展示给用户看会有理解障碍（像 "调用 get_watermark_content 入参
 * {keywords:[\"项目\"]}"）。本文件把它们翻译成"用户视角的业务语言"。
 *
 * 两件事：
 *  1. TOOL_DISPLAY_NAME · 工具名 → 用户看得懂的动作描述
 *  2. summarizeArgs / summarizeResult · 把入参/结果浓缩成 1 行摘要
 *
 * 后端如果将来在 tool_start.display_name / display_args 里直接给文案，前端
 * 优先用后端给的；否则走本文件兜底。
 */

/**
 * 工具名 → 用户视角的动作描述
 *
 * 措辞原则（2026-05-15 用户反馈）:
 *   · 用业务语言, 不用开发术语（不出现 mcp / api / query 等）
 *   · 进行态: "正在 + 业务动作", 让用户感知"AI 在干什么"（参考截图 27）
 *   · 完成态由 ToolCallsSummary 渲染时改"已 X"
 */
export const TOOL_DISPLAY_NAME: Record<string, string> = {
  // 水印路径
  get_watermark_content: '正在查询水印内容',
  // 路径一 §4.4
  parse_search_query_by_nlp: '正在解析你的描述',
  ai_search_photos: '正在按条件搜索照片',
  search_photos: '正在搜索照片',
  // 路径一 §4.1
  query_photos: '正在抽样照片',
  // 路径一 §4.2
  get_photo_ranking_by_user: '正在统计成员拍摄量',
  // §4.2 成员维度 (虚拟工具)
  list_group_users_compact: '正在按成员搜索照片',
  get_group_users_page: '正在拉取团队成员',
  // 已聚合工具池
  get_field_values: '正在查询水印字段值',
  get_time_distribution: '正在统计时间分布',
  get_team_members: '正在查询团队成员',
  search_values_by_keyword: '正在按关键词搜索水印',
  // organizationMcp 系列（按部门 + 细分到成员场景）
  get_departments_info: '正在拉取部门列表',
  list_department_users: '正在拉取部门成员',
  get_department_childes: '正在拉取部门列表',
  get_department_children: '正在拉取部门列表',
  get_group_users_by_page: '正在拉取团队成员',
  get_department_users_by_page: '正在拉取部门成员',
  get_department_users_by_user_id: '正在查询成员所属部门',
  get_full_users_by_ids: '正在获取成员详情',
  search_group_member_by_name: '正在搜索成员',
  get_group_member_by_user_id: '正在查询成员资料',
  get_group_users_counts: '正在统计成员数量',
  // 建夹相关
  create_book: '正在创建文件夹',
  batch_create_with_rule: '正在创建文件夹',
  create_with_rule: '正在创建文件夹',
  save_rule: '正在保存分类规则',
  search_group_books: '正在搜索团队文件夹',
  get_group_books: '正在查询团队文件夹',
  batch_get_book_info: '正在批量查询文件夹信息'
}

/** 取工具的展示名 · 不在表里就原样返回 */
export function displayToolName(toolName: string | undefined): string {
  if (!toolName) return '调用工具'
  return TOOL_DISPLAY_NAME[toolName] ?? toolName
}

const MAX_LABEL_LEN = 32

/**
 * 把 tool_start 的 args 字符串浓缩成 1 行展示参数。
 *
 * SSE 协议里 args 是 LLM 给的 JSON 字符串。绝大多数工具入参形如：
 *   {"keywords":["项目"]} / {"query":"张三的照片"} / {"group_id":"...","page_size":100}
 *
 * 提炼策略：
 *   · 解析 JSON 失败 → 截断原串
 *   · 优先提取业务相关字段（keywords / query / user_ids / department_ids / times）
 *   · 都没命中 → 取所有 string/number 值拼接
 */
export function summarizeArgs(rawArgs: unknown): string {
  if (!rawArgs) return ''
  let obj: Record<string, unknown> | null = null
  if (typeof rawArgs === 'string') {
    try {
      obj = JSON.parse(rawArgs)
    } catch {
      return truncate(rawArgs, MAX_LABEL_LEN)
    }
  } else if (typeof rawArgs === 'object') {
    obj = rawArgs as Record<string, unknown>
  }
  if (!obj) return ''

  const parts: string[] = []
  // 关键业务字段优先
  if (Array.isArray(obj.keywords) && obj.keywords.length > 0) {
    parts.push(`关键词:${(obj.keywords as unknown[]).join('/')}`)
  }
  if (typeof obj.query === 'string' && obj.query) {
    parts.push(`「${obj.query}」`)
  }
  if (typeof obj.keyword === 'string' && obj.keyword) {
    parts.push(`「${obj.keyword}」`)
  }
  if (Array.isArray(obj.user_ids) && obj.user_ids.length > 0) {
    parts.push(`${obj.user_ids.length} 位成员`)
  }
  if (Array.isArray(obj.department_ids) && obj.department_ids.length > 0) {
    parts.push(`${obj.department_ids.length} 个部门`)
  }
  if (typeof obj.department_id === 'string' && obj.department_id) {
    parts.push(`部门 ${obj.department_id}`)
  }
  if (typeof obj.top_n === 'number' && obj.top_n > 0) {
    parts.push(`Top ${obj.top_n}`)
  }
  if (
    Array.isArray((obj as { times?: unknown[] }).times) &&
    ((obj as { times: unknown[] }).times || []).length > 0
  ) {
    parts.push(`${((obj as { times: unknown[] }).times || []).length} 个时间段`)
  }

  if (parts.length > 0) return truncate(parts.join(' · '), MAX_LABEL_LEN)

  // 兜底：屏蔽工程字段（group_id / limit / offset / page / page_size 等
  // 用户不关心的内部参数），不再回退到 "group_id=xxx, limit=50, of..." 这种
  // 工程串。如果一个工具入参全是工程字段就直接返回空 label。
  const ENGINEERING_KEYS = new Set([
    'group_id',
    'groupId',
    'group_id_real',
    'groupIdReal',
    'user_id',
    'userId',
    'limit',
    'offset',
    'page',
    'page_size',
    'pageSize',
    'cursor',
    'token',
    'source'
  ])
  const flat = Object.entries(obj)
    .filter(([k, v]) => {
      if (ENGINEERING_KEYS.has(k)) return false
      return typeof v === 'string' || typeof v === 'number'
    })
    .map(([k, v]) => `${k}=${v}`)
    .join(', ')
  return truncate(flat, MAX_LABEL_LEN)
}

const MAX_RESULT_LEN = 48

/**
 * 把 tool_end 的 result 字符串浓缩成 1 行结果摘要。
 *
 * SSE 协议里 result 也是 JSON 字符串（photoMcp 的 result 通常长这样）：
 *   {"status":"OK","total":85,"photos":[...]}
 *   {"title_hits":[{"title":"项目","count":21}], "content_hits":[]}
 *   {"members":[...], "total":15}
 *
 * 提炼策略：
 *   · 解析 JSON 失败 → 原串截断
 *   · 优先提取 total / count / photos.length / hits.length 等"数量类"字段
 *   · 标记失败：status !== "OK" / ok === false
 */
export function summarizeResult(rawResult: unknown, ok = true): string {
  if (!ok) return '失败'
  if (!rawResult) return '完成'
  let obj: Record<string, unknown> | null = null
  if (typeof rawResult === 'string') {
    try {
      obj = JSON.parse(rawResult)
    } catch {
      return truncate(rawResult, MAX_RESULT_LEN)
    }
  } else if (typeof rawResult === 'object') {
    obj = rawResult as Record<string, unknown>
  }
  if (!obj) return '完成'

  // 失败状态优先
  if (typeof obj.status === 'string' && obj.status !== 'OK') {
    return `状态: ${obj.status}`
  }

  const hints: string[] = []
  if (typeof obj.total === 'number') hints.push(`${obj.total} 张`)
  else if (Array.isArray(obj.photos)) hints.push(`${obj.photos.length} 张`)

  if (Array.isArray(obj.title_hits)) {
    hints.push(`${obj.title_hits.length} 个字段命中`)
  }
  if (Array.isArray(obj.content_hits)) {
    hints.push(`${obj.content_hits.length} 个内容命中`)
  }
  if (Array.isArray(obj.members)) hints.push(`${obj.members.length} 位成员`)
  if (Array.isArray(obj.users)) hints.push(`${obj.users.length} 位成员`)
  if (Array.isArray(obj.departments)) {
    hints.push(`${obj.departments.length} 个部门`)
  }
  if (Array.isArray(obj.children)) hints.push(`${obj.children.length} 个子部门`)
  if (Array.isArray(obj.rankings)) hints.push(`${obj.rankings.length} 项排行`)

  if (hints.length > 0) return truncate(hints.join(' / '), MAX_RESULT_LEN)

  return '完成'
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return s.slice(0, max - 1) + '…'
}
